import { m } from "#/paraglide/messages";
import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { Result } from "better-result";
import { toResult } from "#/modules/util.auth/api-errors";
import {
  fetchPastSchoolLessonsBeforeTodayPaginated,
  fetchSchoolLessonsForOverviewCardLayout,
  fetchSchoolLessonsMatchingLessonsOverviewTabFilters,
} from "#/modules/school.lessen/api.lessen.server";
import { groupLessonsByScheduledCalendarDay } from "#/modules/school.lessen/group-lessons-by-scheduled-calendar-day";
import { isLessonsOverviewTableTab } from "#/modules/school.lessen/params.lessen-overview";
import type { LessonsSearch } from "#/modules/school.lessen/schema.search";
import { client } from "#/modules/util.fetch-client/fetch-client.server";

/**
 * Lessons overview loader: takes the typed `date` + `dateFilterValue` (the page's `loaderDeps`,
 * narrowed from its `validateSearch` schema — see `schema.search.ts`) directly, no more raw
 * request-string parsing. Card layout (Vandaag/Morgen/Toekomst) always loads; the table layout
 * (Verleden/Alles) only loads when the current tab uses it.
 */
export const getLessonsOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: LessonsSearch) => data)
  .handler(async ({ data }) => {
    const [cardResult, pastResult] = await Promise.all([
      fetchSchoolLessonsForOverviewCardLayout(),
      // The card fetch only covers today onwards; one past row is enough to know whether the
      // schooljaar has any history, so the empty state doesn't fire on a school with only past lessen.
      fetchPastSchoolLessonsBeforeTodayPaginated(1, 1),
    ]);
    if (Result.isError(cardResult)) throw cardResult.error;
    if (Result.isError(pastResult)) throw pastResult.error;
    const { todayLessons, tomorrowLessons, futureLessons } = groupLessonsByScheduledCalendarDay(
      cardResult.value.data ?? [],
    );
    const hasPastLessons = (pastResult.value.count ?? 0) > 0;

    const tableResult = isLessonsOverviewTableTab(data)
      ? await fetchSchoolLessonsMatchingLessonsOverviewTabFilters(data)
      : null;
    if (tableResult && Result.isError(tableResult)) throw tableResult.error;
    const tableLessons = tableResult ? (tableResult.value.data ?? []) : null;

    return { todayLessons, tomorrowLessons, futureLessons, hasPastLessons, tableLessons };
  });

export const deleteLessons = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: { lessonIds: string }) => data)
  .handler(async ({ data }) => {
    if (!data.lessonIds) {
      throw new Error(m["lessen.geen_les_geselecteerd"]());
    }

    const result = toResult(
      await client.DELETE("/school/lessen", {
        body: [data.lessonIds],
      }),
    );

    if (Result.isError(result)) return apiFailure(result.error);
  });
